export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk|Unable to preload CSS|Load failed/i.test(
    message,
  );
}

export async function retryLazyImport<T>(
  loader: () => Promise<T>,
  attempts = 4,
): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await loader();
    } catch (error) {
      last = error;
      if (!isChunkLoadError(error) || attempt === attempts - 1) {
        throw error;
      }
      await new Promise(resolve =>
        setTimeout(resolve, Math.min(8000, 400 * 2 ** attempt)),
      );
    }
  }
  throw last;
}

const RELOAD_KEY = 'cd-chunk-reload';

export function reloadOnceAfterChunkError(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_KEY) === '1') {
      return false;
    }
    sessionStorage.setItem(RELOAD_KEY, '1');
  } catch {
    // private mode
  }
  window.location.reload();
  return true;
}

export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore
  }
}

export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('vite:preloadError', event => {
    event.preventDefault?.();
    reloadOnceAfterChunkError();
  });

  window.addEventListener('online', () => {
    try {
      if (sessionStorage.getItem(RELOAD_KEY) === '1') {
        sessionStorage.removeItem(RELOAD_KEY);
        window.location.reload();
      }
    } catch {
      // ignore
    }
  });
}
